import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import styled from '@emotion/styled';
import {
  Dialog,
  DialogContent,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import { Functionality } from '../..';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { enableSso } from '../../../../../../services/orgs';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';

const NAME = 'Microsoft Entra SSO';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  org,
  open,
  onClose
}: {
  org: IOrganization;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  const enabledForEmployees =
    org.sso.enabledForEndUsers && org.sso.provider === SsoProviderType.MS;
  const enabledForAdmins =
    org.sso.enabledForAdmins && org.sso.provider === SsoProviderType.MS;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>

      <Content>
        <FormControlLabel
          label={t('Enable Single-Sign On for Employees')}
          control={
            <Switch
              checked={enabledForEmployees}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: !enabledForEmployees,
                  enabledForAdmins: enabledForAdmins,
                  provider: SsoProviderType.MS
                });
              }}
              color="primary"
            />
          }
        />

        <FormControlLabel
          label={t('Enable Single-Sign On for Admins')}
          control={
            <Switch
              checked={enabledForAdmins}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: enabledForEmployees,
                  enabledForAdmins: !enabledForAdmins,
                  provider: SsoProviderType.MS
                });
              }}
              color="primary"
            />
          }
        />
      </Content>
    </Dialog>
  );
};

export const MicrosoftEntraSsoSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const active =
    (org.sso.enabledForAdmins || org.sso.enabledForEndUsers) &&
    org.sso.provider === SsoProviderType.MS;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/microsoft/ms_entra_icon.svg"
      active={active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog org={org} open={open} onClose={onClose} />
      )}
    />
  );
};
